<template>
    <div class="product-rating">
        <span class="text-right">{{ $t('ProductRatingControls.text') }}</span>

        <div
            class="product-rating__btn-group btn-group"
            role="group"
            :aria-label="$t('ProductRatingControls.buttonGroup')"
        >
            <button
                type="button"
                :disabled="likeDisabled"
                :aria-disabled="likeDisabled"
                :aria-label="$t('ProductRatingControls.likeLabel')"
                role="button"
                @click="likeProduct"
                class="btn btn-outline-secondary product-rating__button product-rating__button-like center-content"
                :class="{ 'product-rating__button--active': rating == 1 }"
                v-tooltip="positiveTooltip"
            >
                <IconSvg filepath="thumbs-up.svg" class="product-rating__icon" />
            </button>

            <span
                class="product-rating__button-separator"
                :class="{ 'product-rating__button-separator--disabled': alreadyRated }"
            ></span>

            <button
                type="button"
                :disabled="dislikeDisabled"
                :aria-disabled="dislikeDisabled"
                :aria-label="$t('ProductRatingControls.dislikeLabel')"
                role="button"
                @click="dislikeProduct"
                class="btn btn-outline-secondary product-rating__button product-rating__button-dislike center-content"
                :class="{ 'product-rating__button--active': rating == 0 }"
                v-tooltip="negativeTooltip"
            >
                <IconSvg filepath="thumbs-up.svg" class="product-rating__icon product-rating__icon-dislike" />
            </button>
        </div>

        <Modal v-if="showFeedback" :width="360" hide-footer centered>
            <ProductRatingFeedback
                :errors="errors"
                :minlength="negativeReasonMinlength"
                :rating="rating"
                @submit="submitRating"
                @cancel="cancelNegative"
            />
        </Modal>
    </div>
</template>

<script>
import ProductRatingFeedback from '@/components/product/rating/ProductRatingFeedback';
import Modal from '@/components/Modal';

export default {
    name: 'ProductRatingControls',

    components: {
        Modal,
        ProductRatingFeedback
    },

    data() {
        return {
            tooltipOptions: {
                position: 'bottom',
                offset: -10,
                delay: 500,
                helpCursor: false,
                ignoreDisabled: false,
                onUpdate(element, bindings, tooltipElement) {
                    if (element.disabled && tooltipElement) {
                        tooltipElement.remove();
                    }
                }
            },
            showFeedback: false,
            rating: null,
            errors: [],
            negativeReasonMinlength: 150
        };
    },

    props: {
        productId: {
            type: Number,
            required: true
        },

        productCategory: {
            type: String,
            required: true
        }
    },

    computed: {
        ratingId() {
            return `${this.productCategory} - ${this.productId}`;
        },
        positiveTooltip() {
            return {
                ...this.tooltipOptions,
                content: this.$t('ProductRatingControls.likeTooltip')
            };
        },
        negativeTooltip() {
            return {
                ...this.tooltipOptions,
                content: this.$t('ProductRatingControls.dislikeTooltip')
            };
        },
        alreadyRated() {
            return this.rating != null;
        },
        likeDisabled() {
            return this.alreadyRated && this.rating == 1;
        },
        dislikeDisabled() {
            //Trickier! Should we allow changing like to dislike? Then
            //return this.alreadyRated && this.rating == 0;
            return this.alreadyRated;
        }
    },

    methods: {
        likeProduct() {
            this.rating = 1;
            this.showFeedback = true;
        },
        dislikeProduct() {
            this.rating = 0;
            this.showFeedback = true;
        },
        async submitRating(data) {
            const { productId, rating } = this;
            this.errors = [];

            if (rating === 0 && (data.message || '').trim().length < this.negativeReasonMinlength) {
                this.errors = [this.$t('ProductRating.feedbackLengthConstraint')];
                return;
            }

            try {
                await this.$axios.post('shop/rate-product', {
                    productId: productId,
                    rating: rating,
                    message: data.message,
                    email: data.email,
                    phone: data.phone,
                    voucherNumber: data.voucherNumber
                });

                this.showFeedback = false;
                this.storeRating();
            } catch (e) {
                this.rating = null;
                this.errors = Array.isArray(e) ? e : [e];
            }
        },
        cancelNegative() {
            this.rating = null;
            this.showFeedback = false;
        },
        checkAlreadyRated() {
            if (process.client && !this.$noLocalStorage) {
                const ratingsGiven = localStorage.getItem('ratings');
                if (ratingsGiven) {
                    try {
                        this.rating = (JSON.parse(ratingsGiven) || {})[this.ratingId];
                    } catch (e) {
                        // cannot parse
                    }
                }
            }
        },
        storeRating() {
            if (process.client && !this.$noLocalStorage) {
                const ratingsGiven = localStorage.getItem('ratings');
                try {
                    const parsed = JSON.parse(ratingsGiven);
                    const existingRatings = parsed && parsed.constructor === Object ? parsed : {};
                    existingRatings[this.ratingId] = this.rating;
                    localStorage.setItem('ratings', JSON.stringify(existingRatings));
                } catch (e) {
                    // cannot parse
                }
            }
        }
    },

    beforeMount() {
        this.checkAlreadyRated();
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';

.product-rating {
    $self: &;
    display: flex;
    place-items: center;
    justify-content: flex-end;
    gap: 10px;
    font-size: 16px;

    &__btn-group {
        &:hover,
        &:focus {
            #{$self}__button-separator {
                color: hsl(0, 0%, 78%);
            }
        }
    }

    &__button {
        $button: &;
        padding: 8px 10px;
        border-color: variables.$muted;
        background-color: var(--aside-darker-bg);
        font-size: 1em;

        &[disabled] {
            color: hsl(0, 0%, 55%);
            pointer-events: none;
        }

        &:focus,
        &:hover {
            color: var(--body-color);
            border-color: variables.$muted;
            background-color: var(--border-color);
        }

        &--active {
            opacity: 1 !important;
            background-color: variables.$muted !important;
            color: var(--body-color) !important;
        }
    }

    &__button-separator {
        border-left: 1px solid currentColor;
        z-index: 2;
        margin-top: 6px;
        margin-bottom: 6px;
        margin-left: -1px;

        &--disabled {
            color: hsl(0, 0%, 55%);
        }
    }

    &__button-like {
        border-right: none;
    }

    &__button-dislike {
        border-left: none;
    }

    &__icon-dislike {
        transform: rotate(180deg);
    }

    @include mixins.mobile-only {
        &__button {
            min-width: 48px;
            min-height: 48px;
        }

        &__icon {
            font-size: 1.5em;
        }
    }
}
</style>
